import { useTranslation } from 'react-i18next';

/**
 * R7-021: 3 learning hint cards per module.
 * Each module gets:
 *   1. identity   — 이 공통 모듈은 무엇인가
 *   2. howItWorks — 어떻게 작동하는가
 *   3. howToRead  — 결과를 어떻게 읽는가
 *
 * i18n key pattern: showcase.hints.<moduleKey>.identity / howItWorks / howToRead
 * Falls back to showcase.hints.default.* when module-specific key is missing.
 */

interface LearningHint {
  label: string;
  icon: string;
  text: string;
}

interface Props {
  /** Short module key, e.g. "c001", "validate", "mask" */
  moduleKey: string;
}

export function LearningHints({ moduleKey }: Props) {
  const { t } = useTranslation();

  const safeT = (key: string, fallbackKey: string): string => {
    const val = t(key);
    // i18next returns the key itself when missing — detect that and use fallback
    if (val === key) {
      const fb = t(fallbackKey);
      return fb === fallbackKey ? '' : fb;
    }
    return val;
  };

  const hints: LearningHint[] = [
    {
      label: t('showcase.hints.label.identity'),
      icon: '🔍',
      text: safeT(
        `showcase.hints.${moduleKey}.identity`,
        'showcase.hints.default.identity'
      ),
    },
    {
      label: t('showcase.hints.label.howItWorks'),
      icon: '⚙️',
      text: safeT(
        `showcase.hints.${moduleKey}.howItWorks`,
        'showcase.hints.default.howItWorks'
      ),
    },
    {
      label: t('showcase.hints.label.howToRead'),
      icon: '📊',
      text: safeT(
        `showcase.hints.${moduleKey}.howToRead`,
        'showcase.hints.default.howToRead'
      ),
    },
  ];

  return (
    <section
      aria-label={t('showcase.hints.sectionLabel')}
      style={{
        marginTop: 'var(--spacing-6)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--spacing-3)',
      }}
    >
      {hints.map((hint) => (
        <div
          key={hint.label}
          style={{
            padding: 'var(--spacing-4)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-elevated)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-1)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <span aria-hidden="true">{hint.icon}</span>
            {hint.label}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-xs)',
              color: hint.text
                ? 'var(--color-text-primary)'
                : 'var(--color-text-muted)',
              lineHeight: 1.6,
              fontStyle: hint.text ? 'normal' : 'italic',
            }}
          >
            {/* safeT() always resolves through showcase.hints.default.*, so hint.text is
                never empty here — no further fallback string is needed. */}
            {hint.text}
          </p>
        </div>
      ))}
    </section>
  );
}
